// @ts-nocheck
import React from 'react';
import { UserCircle, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  getPremadeProfileFields,
  SELF_RATING_FIELD,
  DUPR_FIELD,
  PROFILE_FIELD_IDS,
} from '@staty/shared/utils/profileFieldCatalog';
import { isIndividualSport } from '@staty/shared/utils/sportCategory';
import CustomFieldAdder from '@components/CustomFieldAdder/CustomFieldAdder';

// Profile fields are an org-wide identity concept, so the per-roster "Jersey #"
// type doesn't apply here. Otherwise matches LeagueSecondStep's defaults.
const CUSTOM_PROFILE_FIELD_TYPES = [
  { value: 'text', label: 'Text', icon: '📝' },
  { value: 'select', label: 'Dropdown', icon: '📋' },
  { value: 'textarea', label: 'Text Area', icon: '📄' },
  { value: 'phone', label: 'Phone', icon: '📱' },
  { value: 'date', label: 'Date', icon: '📅' },
];

const PREMADE_PROFILE_IDS = new Set(Object.values(PROFILE_FIELD_IDS));

/**
 * Self-contained editor for the per-org Create Profile field list.
 *
 * Owns all derivation (Date-of-birth / Rating radio choices, Required
 * states) and the helpers that mutate the array (add, remove, toggle
 * required, swap DOB choice, swap Rating source). The parent provides
 * the current `profileFields` array, the org's `sport`, and a single
 * `onChange(nextFields)` callback — it doesn't need to know about the
 * radios, the catalog, or the Self-Rating / DUPR collapse.
 *
 * Followed AddForm's pattern: schema-in, change-out, all the UI logic
 * lives in here.
 */
export default function ProfileFieldsEditor({ profileFields = [], orgSport, onChange }) {
  // Premade catalog scoped to the org's sport. Single source of truth
  // shared with apps/app via packages/shared/utils/profileFieldCatalog.ts.
  const premadeFields = getPremadeProfileFields(orgSport);
  const fieldByIdx = (id) => profileFields.findIndex((f) => f.fieldId === id);

  // DOB state derivation ---------------------------------------------------
  const birthDateIdx = fieldByIdx(PROFILE_FIELD_IDS.BIRTH_DATE);
  const birthYearIdx = fieldByIdx(PROFILE_FIELD_IDS.BIRTH_YEAR);
  const dobChoice =
    birthDateIdx >= 0 ? 'birth_date'
    : birthYearIdx >= 0 ? 'birth_year'
    : 'none';
  const dobRequired =
    dobChoice === 'birth_date' ? !!profileFields[birthDateIdx].required
    : dobChoice === 'birth_year' ? !!profileFields[birthYearIdx].required
    : false;

  // Rating state derivation ------------------------------------------------
  const selfRatingIdx = fieldByIdx(PROFILE_FIELD_IDS.SELF_RATING);
  const duprIdx = fieldByIdx(PROFILE_FIELD_IDS.DUPR_ID);
  const ratingChoice =
    selfRatingIdx >= 0 ? 'self_rating'
    : duprIdx >= 0 ? 'dupr'
    : 'none';
  const ratingRequired =
    ratingChoice === 'self_rating' ? !!profileFields[selfRatingIdx].required
    : ratingChoice === 'dupr' ? !!profileFields[duprIdx].required
    : false;

  // Mutators (all call onChange with the next array) -----------------------
  const addField = (field) => {
    onChange([...profileFields, { ...field }]);
  };
  const removeFieldAt = (index) => {
    const next = [...profileFields];
    next.splice(index, 1);
    onChange(next);
  };
  const setRequiredAt = (index, required) => {
    onChange(profileFields.map((f, i) => (i === index ? { ...f, required } : f)));
  };

  // Mutually-exclusive Birth Date / Birth Year / none. Drop both, then add
  // the chosen field; preserve `required` so flipping between Date and
  // Year doesn't silently demote a required field.
  const setDobChoice = (next) => {
    const wasRequired = dobRequired;
    const without = profileFields.filter(
      (f) => f.fieldId !== PROFILE_FIELD_IDS.BIRTH_DATE && f.fieldId !== PROFILE_FIELD_IDS.BIRTH_YEAR
    );
    if (next === 'none') return onChange(without);
    const field = premadeFields.find((f) => f.fieldId === next);
    if (!field) return;
    onChange([...without, { ...field, required: wasRequired }]);
  };
  const setDobRequired = (required) => {
    if (dobChoice === 'none') return;
    const idx = dobChoice === 'birth_date' ? birthDateIdx : birthYearIdx;
    setRequiredAt(idx, required);
  };

  // Mutually-exclusive Self-Rating / DUPR / none for the rating source.
  // Both add their respective field (`self_rating` or `dupr_id`) directly
  // to profileFields — no per-org boolean.
  const setRatingChoice = (next) => {
    const wasRequired = ratingRequired;
    const without = profileFields.filter(
      (f) => f.fieldId !== PROFILE_FIELD_IDS.SELF_RATING && f.fieldId !== PROFILE_FIELD_IDS.DUPR_ID
    );
    if (next === 'self_rating') return onChange([...without, { ...SELF_RATING_FIELD, required: wasRequired }]);
    if (next === 'dupr')         return onChange([...without, { ...DUPR_FIELD,        required: wasRequired }]);
    onChange(without);
  };
  const setRatingRequired = (required) => {
    if (ratingChoice === 'self_rating') return setRequiredAt(selfRatingIdx, required);
    if (ratingChoice === 'dupr')        return setRequiredAt(duprIdx, required);
  };

  // -------------------------------------------------------------------------
  return (
    <Card className="shadow-sm border-0">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserCircle className="h-5 w-5" />
          Create Profile
        </CardTitle>
        <CardDescription>
          Pick the fields users must fill out before they can start a registration in this organization.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1">
          <Label>Profile fields</Label>
          <p className="text-xs text-muted-foreground">
            Toggle which fields a player must fill out. Required fields block registration until completed.
          </p>
        </div>

        <div className="divide-y rounded-md border">
          {premadeFields.map((field) => {
            const fieldId = field.fieldId;
            if (fieldId === PROFILE_FIELD_IDS.BIRTH_YEAR) return null; // handled inside the DOB radio
            if (fieldId === PROFILE_FIELD_IDS.SELF_RATING) return null; // handled inside the Rating radio
            if (fieldId === PROFILE_FIELD_IDS.DUPR_ID) return null; // handled inside the Rating radio

            // Birth Date row → renders the 3-option DOB radio group
            if (fieldId === PROFILE_FIELD_IDS.BIRTH_DATE) {
              return (
                <div key="dob" className="px-4 py-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <Label className="text-sm font-medium">Date of birth</Label>
                      <RadioGroup
                        value={dobChoice}
                        onValueChange={(v) => setDobChoice(v)}
                        className="mt-2 space-y-2"
                      >
                        <label htmlFor="dob-date" className="flex items-center gap-2 text-sm cursor-pointer">
                          <RadioGroupItem id="dob-date" value="birth_date" />
                          <span>Birth Date</span>
                        </label>
                        <label htmlFor="dob-year" className="flex items-start gap-2 text-sm cursor-pointer">
                          <RadioGroupItem id="dob-year" value="birth_year" className="mt-1" />
                          <span>
                            Birth Year
                            <span className="block text-xs text-muted-foreground">
                              Used for age divisions, computed against Dec 31 of the event year.
                            </span>
                          </span>
                        </label>
                        <label htmlFor="dob-none" className="flex items-center gap-2 text-sm cursor-pointer">
                          <RadioGroupItem id="dob-none" value="none" />
                          <span>No DOB field</span>
                        </label>
                      </RadioGroup>
                    </div>
                    <div className="flex items-center gap-2 pt-1">
                      <span className="text-xs text-muted-foreground">Required</span>
                      <Switch
                        checked={dobRequired}
                        disabled={dobChoice === 'none'}
                        onCheckedChange={(checked) => setDobRequired(checked)}
                      />
                    </div>
                  </div>
                </div>
              );
            }

            // Standard premade row
            const idx = fieldByIdx(fieldId);
            const included = idx >= 0;
            const required = included ? !!profileFields[idx].required : false;
            return (
              <div key={fieldId} className="flex items-center justify-between gap-4 px-4 py-3">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <Switch
                    checked={included}
                    onCheckedChange={(checked) => (checked ? addField(field) : removeFieldAt(idx))}
                  />
                  <Label className="text-sm font-medium cursor-pointer">{field.name}</Label>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Required</span>
                  <Switch
                    checked={required}
                    disabled={!included}
                    onCheckedChange={(checked) => setRequiredAt(idx, checked)}
                  />
                </div>
              </div>
            );
          })}

          {/* Custom fields — share component with the registration edit page's Player Form Fields section */}
          {(() => {
            const customRows = profileFields
              .map((f, idx) => ({ field: f, idx }))
              .filter(({ field }) => field?.fieldId && !PREMADE_PROFILE_IDS.has(field.fieldId));
            if (customRows.length === 0) return null;
            return (
              <div className="bg-muted/50 px-4 py-3">
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Custom fields
                </Label>
                <div className="mt-2 divide-y rounded-md border bg-card">
                  {customRows.map(({ field, idx }) => (
                    <div key={`custom-${idx}`} className="flex items-center justify-between gap-4 px-4 py-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <Label className="truncate text-sm font-medium">{field.name || field.fieldId}</Label>
                          <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                            {field.fieldType}
                          </span>
                        </div>
                        {field.helperText && (
                          <p className="mt-0.5 truncate text-xs text-muted-foreground">{field.helperText}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">Required</span>
                          <Switch
                            checked={!!field.required}
                            onCheckedChange={(checked) => setRequiredAt(idx, checked)}
                          />
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label={`Remove ${field.name}`}
                          onClick={() => removeFieldAt(idx)}
                        >
                          <Trash2 className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {isIndividualSport(orgSport) && (
            <div key="rating" className="px-4 py-3">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <Label className="text-sm font-medium">Rating source</Label>
                  <RadioGroup
                    value={ratingChoice}
                    onValueChange={(v) => setRatingChoice(v)}
                    className="mt-2 space-y-2"
                  >
                    <label htmlFor="rating-self" className="flex items-center gap-2 text-sm cursor-pointer">
                      <RadioGroupItem id="rating-self" value="self_rating" />
                      <span>Self-Rating</span>
                    </label>
                    <label htmlFor="rating-dupr" className="flex items-start gap-2 text-sm cursor-pointer">
                      <RadioGroupItem id="rating-dupr" value="dupr" className="mt-1" />
                      <span>
                        DUPR integration
                        <span className="block text-xs text-muted-foreground">
                          We'll auto-fetch the player's DUPR rating. Integration not yet enabled. Picking this only flags the org for now.
                        </span>
                      </span>
                    </label>
                    <label htmlFor="rating-none" className="flex items-center gap-2 text-sm cursor-pointer">
                      <RadioGroupItem id="rating-none" value="none" />
                      <span>No rating field</span>
                    </label>
                  </RadioGroup>
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <span className="text-xs text-muted-foreground">Required</span>
                  <Switch
                    checked={ratingRequired}
                    disabled={ratingChoice === 'none'}
                    onCheckedChange={(checked) => setRatingRequired(checked)}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Add a custom field — same UI as the registration edit page's Player Form Fields section */}
        <div className="rounded-md border bg-card p-4">
          <div className="mb-3">
            <Label className="text-sm font-medium">Add a custom field</Label>
            <p className="text-xs text-muted-foreground">
              Collect extra info from every player when they create their profile (e.g. T-shirt size, emergency note).
            </p>
          </div>
          <CustomFieldAdder
            theme="slate"
            fieldTypeOptions={CUSTOM_PROFILE_FIELD_TYPES}
            onAdd={(f) => addField(f)}
          />
        </div>
      </CardContent>
    </Card>
  );
}
